import React from 'react';

import {
    Comment,
    DeleteCommentInput,
    DeleteCommentOutput,
    ModifyCommentInput,
    ModifyCommentOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Button from '@amzn/meridian/button';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';

import { Link } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { ConfirmationModal, LoaderForBtn } from 'src/components/ConfirmationModal';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { PHONE_TOOL_URL } from 'src/constants/phoneTool';
import { AppContext } from 'src/contexts/appContext';
import { IToast } from 'src/types/IToaster';
import { formatDateFriendly } from 'src/util/dateTimeUtil';

import './CommentForm.scss';

interface ICommentFormProps extends Comment {
    updateCommentModifiedStateHandler: (
        updatedComment: string,
        commentId: number,
        creationDate: Date,
        newCommentId: number
    ) => void;
    deleteCommentStateHandler: (commentId: number) => void;
}

export const CommentForm = ({
    updateCommentModifiedStateHandler,
    deleteCommentStateHandler,
    comment,
    commentId,
    createdBy,
    creationDate
}: ICommentFormProps) => {
    const [isEditMode, setIsEditMode] = React.useState<boolean>(false);
    const [commentText, setCommentText] = React.useState('');
    const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const { userInfo, setTopAlertMsg, setToasts } = React.useContext(AppContext);

    const openEditBox = () => {
        setCommentText(comment as string);
        setIsEditMode(true);
    };

    const closeEditBox = () => {
        setCommentText(comment as string);
        setIsEditMode(false);
    };

    const deleteCommentHandler = () => {
        contractConfigurationClient
            .deleteComment(
                DeleteCommentInput.clone({
                    commentId: commentId,
                    user: userInfo.username
                })
            )
            .subscribe(
                (response: DeleteCommentOutput) => {
                    if (response) {
                        deleteCommentStateHandler(commentId as number);
                        setOpenDeleteModal(false);
                        const toastMessage = 'Deletion successful';
                        const toast: IToast = {
                            id: 'comment-deleted',
                            timeout: DEFAULT_TOASTER_TIMEOUT * 2,
                            message: toastMessage
                        };
                        setToasts([toast]);
                    }
                },
                (error: UnknownCoralError) => {
                    console.error('DeleteComment Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    const editCommentHandler = () => {
        setIsLoading(true);
        contractConfigurationClient
            .modifyComment(
                ModifyCommentInput.clone({
                    commentId: commentId,
                    newComment: commentText.trim(),
                    user: userInfo.username
                })
            )
            .subscribe(
                (response: ModifyCommentOutput) => {
                    if (response) {
                        updateCommentModifiedStateHandler(
                            commentText as string,
                            commentId as number,
                            response.comment?.creationDate as Date,
                            response.comment?.commentId as number
                        );
                        setIsLoading(false);
                        setIsEditMode(false);
                    }
                },
                (error: UnknownCoralError) => {
                    console.error('ModifyComment Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };
    return (
        <div className="commentFormContainer">
            <Card spacingInset="400">
                <Column spacing="400">
                    <Row spacing="300">
                        <Link to={{ pathname: `${PHONE_TOOL_URL}${createdBy}` }} target="_blank">
                            <img
                                className="profile-photo"
                                src={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${createdBy}`}
                            ></img>
                        </Link>
                        <Link
                            className="userinfo-username"
                            to={{ pathname: `${PHONE_TOOL_URL}${createdBy}` }}
                            target="_blank"
                        >
                            <Text color="primary" type="h100">
                                @{createdBy}
                            </Text>
                        </Link>
                    </Row>
                    <Text type="b100">{formatDateFriendly(creationDate as Date)}</Text>
                    {!isEditMode ? (
                        <Column>
                            <Text type="b300" className="comment-content">
                                {comment}
                            </Text>
                            {userInfo.username == createdBy && (
                                <Row>
                                    <Button className="edit-button" type="link" onClick={openEditBox}>
                                        <Icon tokens={editTokens} />
                                    </Button>
                                    <Button
                                        className="trash-button"
                                        onClick={() => setOpenDeleteModal(true)}
                                        type="link"
                                    >
                                        <Icon className="button-icon" tokens={trashTokens} />
                                    </Button>
                                </Row>
                            )}
                        </Column>
                    ) : (
                        <>
                            <Row width="100%" spacingInset="300 none 400 none" widths={['100%']}>
                                <Textarea resize="none" value={commentText || ''} onChange={setCommentText} />
                            </Row>

                            <Row alignmentHorizontal="end" widths="fit">
                                <Button onClick={closeEditBox} type="secondary" size="small">
                                    Cancel
                                </Button>
                                <Button
                                    className="savechanges"
                                    onClick={editCommentHandler}
                                    disabled={
                                        commentText.trim() == comment || commentText.trim().length == 0 || isLoading
                                    }
                                    type="primary"
                                    size="small"
                                >
                                    {isLoading ? LoaderForBtn : 'Save changes'}
                                </Button>
                            </Row>
                        </>
                    )}
                </Column>
            </Card>

            <ConfirmationModal
                isOpen={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
                title="Delete"
                body="Are you sure you want to delete?"
                showComments={false}
                primaryButtonText="Yes, delete it"
                secondaryButtonText="Go back"
                primaryButtonOnClick={deleteCommentHandler}
                secondaryButtonOnClick={() => setOpenDeleteModal(false)}
            />
        </div>
    );
};
